/**
 * Data Quality validation API client.
 *
 * The axios base URL already ends in /api, so paths here are relative to it —
 * e.g. /finance/validation/{company_id}.
 *
 * A 404 means nothing has been validated for this company yet. That is NOT an
 * error: both calls resolve to `null` so callers render nothing.
 */

import apiClient from '../api';
import type {
  ValidationUiResponse,
  ValidationIssueResponse,
  FieldCoverageResponse,
} from '../../types/dataQuality';

/** Rethrow anything that is not a 404; a 404 becomes `null`. */
const nullOn404 = (error: any) => {
  const status = error?.response?.status ?? error?.status;
  if (status === 404) return null;
  throw error;
};

/**
 * As `nullOn404`, plus 503.
 *
 * The field coverage endpoint answers 503 when the read fails. The matrix is
 * advisory supplementary detail, so an unavailable read is treated exactly
 * like "nothing stored": render nothing, never an error state on the page.
 */
const nullOnMissing = (error: any) => {
  const status = error?.response?.status ?? error?.status;
  if (status === 404 || status === 503) return null;
  throw error;
};

/**
 * Level 1 + 2 — the panel and console: badge, headline and the issue rows.
 *
 * `companyId` is the same id the company detail page uses (e.g. `ind_DABUR`).
 * Never request `?view=full` — it repeats one problem once per year and is
 * for debugging only.
 *
 * `maxIssues` raises the summary's issue cap above the backend's current
 * default of 5 (`ui_summary(max_issues=5)`), which is enough for the Overview
 * strip but not for the console's Findings view. Sent unconditionally: a
 * backend that does not yet recognise the param ignores it and returns 5, and
 * `issues_truncated` is already the honest fallback for that case, so no
 * feature-detection is needed on this side.
 *
 * Backend route: GET /api/finance/validation/{company_id}?view=ui&max_issues=
 */
export const getValidationSummary = async (
  companyId: string,
  includePrevious: boolean = false,
  maxIssues?: number
): Promise<ValidationUiResponse | null> => {
  const params = new URLSearchParams({ view: 'ui' });
  if (includePrevious) params.append('include_previous', 'true');
  if (maxIssues != null) params.append('max_issues', String(maxIssues));

  try {
    const response = await apiClient.get<ValidationUiResponse>(
      `/finance/validation/${encodeURIComponent(companyId)}?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    return nullOn404(error);
  }
};

/**
 * Level 3 — one issue in full. Called only when a user opens an issue row.
 *
 * Backend route: GET /api/finance/validation/{company_id}/issue/{code}?source=
 */
export const getValidationIssue = async (
  companyId: string,
  code: string,
  source: string
): Promise<ValidationIssueResponse | null> => {
  const params = new URLSearchParams({ source });

  try {
    const response = await apiClient.get<ValidationIssueResponse>(
      `/finance/validation/${encodeURIComponent(companyId)}/issue/${encodeURIComponent(
        code
      )}?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    return nullOn404(error);
  }
};

/**
 * Endpoint 3 — the Field Coverage Matrix for one chain and one year.
 *
 * Called only when a user expands the matrix, never on panel mount.
 *
 * `source` is REQUIRED ("PDF" | "API"): the two chains hold different field
 * sets and the backend answers 400 without it. `year` is optional on the wire,
 * but callers should always pass one — omitting it returns every stored year
 * at once, which for a ten-year company is a very large response.
 *
 * Backend route: GET /api/finance/validation/{company_id}/fields?source=&year=
 */
export const getFieldCoverage = async (
  companyId: string,
  source: string,
  year?: number | null
): Promise<FieldCoverageResponse | null> => {
  const params = new URLSearchParams({ source });
  if (year != null) params.append('year', String(year));

  try {
    const response = await apiClient.get<FieldCoverageResponse>(
      `/finance/validation/${encodeURIComponent(companyId)}/fields?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    // 404 (no stored data for this chain) and 503 (read failure) both mean
    // "nothing to show" for an advisory panel.
    return nullOnMissing(error);
  }
};
